import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Shield, Clock, AlertCircle, Check, CheckCircle, XCircle, RefreshCw,
    Eye, ExternalLink, ShieldCheck, Search, ChevronLeft, ChevronRight,
    FileText, X, Loader2, Maximize2
} from 'lucide-react';
import { kycService } from '../../services/kycService';
import { companyService } from '../../services/companyService';
import { useAuth } from '../../context/AuthContext';
import CustomSelect from '../shared/CustomSelect';

const PAGE_SIZES = [25, 50, 100];

const STATUS_STYLES = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    VERIFIED: 'bg-blue-100 text-blue-800',
    APPROVED: 'bg-green-100 text-green-800',
    REJECTED: 'bg-red-100 text-red-800',
    EXPIRED: 'bg-gray-100 text-gray-700',
};

const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const emptyPage = { content: [], totalElements: 0, totalPages: 0 };

// The queue endpoints return a Spring Page inside the ApiResponse envelope.
const unwrapPage = (response) => {
    const data = response?.success ? response.data : response;
    if (data?.content) {
        return {
            content: data.content,
            totalElements: data.totalElements || 0,
            totalPages: data.totalPages || 0,
        };
    }
    const rows = Array.isArray(data) ? data : [];
    return { content: rows, totalElements: rows.length, totalPages: rows.length ? 1 : 0 };
};

const KycQueuePage = () => {
    const { hasPermission } = useAuth();
    const canVerify = hasPermission('KYC:APPROVE');
    const canApprove = hasPermission('KYC:APPROVE');

    const [activeTab, setActiveTab] = useState('verification');
    const [page, setPage] = useState(emptyPage);
    const [counts, setCounts] = useState({ pendingVerification: 0, pendingApproval: 0 });
    const [loading, setLoading] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [rejectionReasons, setRejectionReasons] = useState([]);

    // Filters — every one of these is sent to the server, nothing is filtered in the browser.
    const [selectedCompany, setSelectedCompany] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

    const [reviewing, setReviewing] = useState(null);
    const [message, setMessage] = useState({ text: '', type: '' });

    const showMessage = useCallback((text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    }, []);

    useEffect(() => {
        companyService.getAll()
            .then((response) => {
                const data = (response.success && response.data) ? response.data : (Array.isArray(response) ? response : []);
                setCompanies(data);
            })
            .catch((error) => console.error('Failed to fetch companies:', error));

        kycService.clientKyc.getRejectionReasons()
            .then((response) => {
                const data = (response.success && response.data) ? response.data : (Array.isArray(response) ? response : []);
                setRejectionReasons(data);
            })
            .catch((error) => console.error('Failed to fetch rejection reasons:', error));
    }, []);

    // Debounce the search box so typing doesn't fire a query per keystroke.
    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput.trim()), 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    // Any filter change invalidates the current page position.
    useEffect(() => { setPageNumber(0); }, [activeTab, selectedCompany, search, pageSize]);

    const queryParams = useMemo(() => ({
        page: pageNumber,
        size: pageSize,
        companyId: selectedCompany || undefined,
        search: search || undefined,
    }), [pageNumber, pageSize, selectedCompany, search]);

    const fetchCounts = useCallback(() => {
        kycService.clientKyc.getQueueCounts({ companyId: selectedCompany || undefined, search: search || undefined })
            .then((response) => {
                const data = (response.success && response.data) ? response.data : response;
                if (data) setCounts({ pendingVerification: data.pendingVerification || 0, pendingApproval: data.pendingApproval || 0 });
            })
            .catch((error) => console.error('Failed to fetch queue counts:', error));
    }, [selectedCompany, search]);

    const fetchQueue = useCallback(async () => {
        try {
            setLoading(true);
            const fetcher = activeTab === 'verification'
                ? kycService.clientKyc.getPending
                : kycService.clientKyc.getPendingApproval;
            setPage(unwrapPage(await fetcher(queryParams)));
        } catch (error) {
            console.error('Failed to fetch KYC queue:', error);
            showMessage(error.message || 'Failed to load the KYC queue', 'error');
            setPage(emptyPage);
        } finally {
            setLoading(false);
        }
    }, [activeTab, queryParams, showMessage]);

    useEffect(() => { fetchQueue(); }, [fetchQueue]);
    useEffect(() => { fetchCounts(); }, [fetchCounts]);

    const refresh = () => { fetchQueue(); fetchCounts(); };

    const handleVerify = async (docId) => {
        await kycService.clientKyc.verify(docId);
        showMessage('Document verified successfully');
        refresh();
    };

    const handleApprove = async (docId) => {
        await kycService.clientKyc.approve(docId);
        showMessage('Document approved successfully');
        refresh();
    };

    const handleReject = async (docId, reasonCode, comment) => {
        await kycService.clientKyc.reject(docId, reasonCode, comment);
        showMessage('Document rejected');
        refresh();
    };

    const openReview = async (row) => {
        // List rows carry no presigned URL — fetch the full record for the preview.
        setReviewing({ ...row, loadingDocument: true });
        try {
            const response = await kycService.clientKyc.getById(row.id);
            const full = (response.success && response.data) ? response.data : response;
            setReviewing({ ...row, ...full, loadingDocument: false });
        } catch (error) {
            console.error('Failed to load document:', error);
            setReviewing({ ...row, loadingDocument: false, loadError: true });
        }
    };

    const total = page.totalElements;
    const rangeStart = total === 0 ? 0 : pageNumber * pageSize + 1;
    const rangeEnd = Math.min(total, (pageNumber + 1) * pageSize);

    return (
        <div className="space-y-4">
            {message.text && (
                <div className={`p-4 rounded-lg flex items-center gap-2 ${
                    message.type === 'error'
                        ? 'bg-red-100 text-red-800 border border-red-200'
                        : 'bg-green-100 text-green-800 border border-green-200'
                }`}>
                    {message.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
                    {message.text}
                </div>
            )}

            {/* Header + tabs */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center">
                            <Shield className="w-5 h-5 text-gray-700" />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">KYC Queue</h2>
                            <p className="text-sm text-gray-500">Review and process KYC document submissions</p>
                        </div>
                    </div>
                    <button
                        onClick={refresh}
                        className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition"
                        title="Refresh"
                    >
                        <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Stat cards — the headline numbers, also act as tab switches */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <StatCard
                        label="Pending Verification"
                        value={counts.pendingVerification}
                        icon={Clock}
                        active={activeTab === 'verification'}
                        onClick={canVerify ? () => setActiveTab('verification') : undefined}
                        tone="bg-yellow-50 border-yellow-200 text-yellow-700"
                        valueTone="text-yellow-800"
                        activeRing="ring-yellow-400"
                    />
                    <StatCard
                        label="Pending Approval"
                        value={counts.pendingApproval}
                        icon={ShieldCheck}
                        active={activeTab === 'approval'}
                        onClick={canApprove ? () => setActiveTab('approval') : undefined}
                        tone="bg-orange-50 border-orange-200 text-orange-700"
                        valueTone="text-orange-800"
                        activeRing="ring-orange-400"
                    />
                </div>

                <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                    {canVerify && (
                        <TabButton
                            active={activeTab === 'verification'}
                            onClick={() => setActiveTab('verification')}
                            icon={Clock}
                            label="Pending Verification"
                            count={counts.pendingVerification}
                            countClass="bg-yellow-100 text-yellow-800"
                        />
                    )}
                    {canApprove && (
                        <TabButton
                            active={activeTab === 'approval'}
                            onClick={() => setActiveTab('approval')}
                            icon={ShieldCheck}
                            label="Pending Approval"
                            count={counts.pendingApproval}
                            countClass="bg-orange-100 text-orange-800"
                        />
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Company</label>
                        <CustomSelect
                            value={selectedCompany}
                            onChange={setSelectedCompany}
                            options={[
                                { value: '', label: 'All Companies' },
                                ...companies.map((company) => ({ value: company.id, label: company.name })),
                            ]}
                            placeholder="All Companies"
                            searchable
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Search client, document type or document number
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Search the whole queue..."
                                className="pl-10 pr-9 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {searchInput && (
                                <button
                                    onClick={() => setSearchInput('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            <Th>Client</Th>
                            <Th>Document</Th>
                            <Th>Document No.</Th>
                            <Th>Company</Th>
                            <Th>Submitted</Th>
                            <Th>Status</Th>
                            <Th align="text-right">Action</Th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                        {loading ? (
                            <tr>
                                <td colSpan={7} className="px-6 py-16 text-center">
                                    <Loader2 className="w-7 h-7 text-blue-600 animate-spin mx-auto" />
                                </td>
                            </tr>
                        ) : page.content.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="px-6 py-16 text-center">
                                    {search || selectedCompany ? (
                                        <>
                                            <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                                            <p className="text-gray-600 font-medium">No matching submissions</p>
                                            <p className="text-sm text-gray-500">Try a different company or search term</p>
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
                                            <p className="text-gray-900 font-medium">All caught up</p>
                                            <p className="text-sm text-gray-500">
                                                {activeTab === 'verification'
                                                    ? 'No documents pending verification'
                                                    : 'No documents pending approval'}
                                            </p>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ) : (
                            page.content.map((row) => (
                                <tr
                                    key={row.id}
                                    onClick={() => openReview(row)}
                                    className="hover:bg-gray-50 transition cursor-pointer"
                                >
                                    <td className="px-6 py-3">
                                        <p className="text-sm font-medium text-gray-900">{row.clientName || '—'}</p>
                                    </td>
                                    <td className="px-6 py-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-900">{row.requirementName || '—'}</span>
                                            {row.mandatory && (
                                                <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-50 text-red-600">
                                                    Required
                                                </span>
                                            )}
                                            {!row.hasDocument && (
                                                <span
                                                    className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 text-gray-500"
                                                    title="No file was uploaded with this submission"
                                                >
                                                    No file
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-3 text-sm text-gray-600 font-mono">
                                        {row.documentNumber || '—'}
                                    </td>
                                    <td className="px-6 py-3 text-sm text-gray-600">{row.companyName || '—'}</td>
                                    <td className="px-6 py-3">
                                        <p className="text-sm text-gray-600">{formatDate(row.createdAt)}</p>
                                        {row.version > 1 && <p className="text-xs text-gray-400">v{row.version}</p>}
                                    </td>
                                    <td className="px-6 py-3">
                                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_STYLES[row.status] || 'bg-gray-100 text-gray-700'}`}>
                                            {row.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-3 text-right">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); openReview(row); }}
                                            className="px-3 py-1.5 text-xs font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition inline-flex items-center gap-1.5"
                                        >
                                            <Eye className="w-3.5 h-3.5" />
                                            Review
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {total > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-4 py-3">
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-gray-600">
                                Showing <span className="font-medium text-gray-900">{rangeStart}–{rangeEnd}</span> of{' '}
                                <span className="font-medium text-gray-900">{total.toLocaleString()}</span>
                            </span>
                            <select
                                value={pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                                className="text-sm border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {PAGE_SIZES.map((size) => (
                                    <option key={size} value={size}>{size} / page</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setPageNumber((p) => Math.max(0, p - 1))}
                                disabled={pageNumber === 0}
                                className="p-1.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <span className="text-sm text-gray-600">
                                Page {pageNumber + 1} of {page.totalPages || 1}
                            </span>
                            <button
                                onClick={() => setPageNumber((p) => Math.min((page.totalPages || 1) - 1, p + 1))}
                                disabled={pageNumber >= (page.totalPages || 1) - 1}
                                className="p-1.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {reviewing && (
                <ReviewModal
                    document={reviewing}
                    rejectionReasons={rejectionReasons}
                    mode={activeTab === 'approval' ? 'approve' : 'verify'}
                    onClose={() => setReviewing(null)}
                    onPositive={activeTab === 'approval' ? handleApprove : handleVerify}
                    onReject={handleReject}
                    onError={(text) => showMessage(text, 'error')}
                />
            )}
        </div>
    );
};

/* ---------- Sub-components ---------- */

const Th = ({ children, align = 'text-left' }) => (
    <th className={`px-6 py-3 ${align} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
        {children}
    </th>
);

const StatCard = ({ label, value, icon: Icon, active, onClick, tone, valueTone, activeRing }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={!onClick}
        className={`text-left border rounded-lg p-4 transition ${tone} ${
            onClick ? 'cursor-pointer hover:brightness-[0.98]' : 'cursor-default'
        } ${active ? `ring-2 ${activeRing}` : ''}`}
    >
        <div className="flex items-center justify-between">
            <p className="text-sm font-medium">{label}</p>
            <Icon className="w-4 h-4 opacity-70" />
        </div>
        <p className={`text-3xl font-bold mt-1 ${valueTone}`}>{(value || 0).toLocaleString()}</p>
    </button>
);

const TabButton = ({ active, onClick, icon: Icon, label, count, countClass }) => (
    <button
        onClick={onClick}
        className={`flex-1 px-4 py-2 rounded-md transition text-sm font-medium ${
            active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-800'
        }`}
    >
        <Icon className="w-4 h-4 inline mr-2" />
        {label}
        {count > 0 && (
            <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${countClass}`}>
                {count.toLocaleString()}
            </span>
        )}
    </button>
);

const DocumentPane = ({ doc }) => {
    const url = doc.documentUrl;

    if (doc.loadingDocument) {
        return (
            <div className="h-full flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-blue-600 animate-spin" />
            </div>
        );
    }

    if (!url) {
        return (
            <div className="h-full flex flex-col items-center justify-center text-gray-400 p-6 text-center">
                <FileText className="w-10 h-10 mb-2 opacity-40" />
                <p className="text-sm">{doc.loadError ? 'Could not load this document' : 'No file uploaded'}</p>
            </div>
        );
    }

    const isImage = /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(url);
    const isPdf = /\.pdf(\?|$)/i.test(url);

    return (
        <div className="relative w-full h-full">
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-lg shadow-sm text-gray-700 transition"
                title="Open full size in a new tab"
            >
                <Maximize2 className="w-4 h-4" />
            </a>
            {isImage ? (
                // Oversized scans need to be pannable, so the image scrolls inside its pane.
                <div className="absolute inset-0 overflow-auto bg-gray-100 flex items-center justify-center">
                    <img src={url} alt={doc.requirementName} className="max-w-full max-h-full object-contain" />
                </div>
            ) : isPdf ? (
                <iframe src={url} title={doc.requirementName} className="absolute inset-0 w-full h-full border-0 bg-gray-100" />
            ) : (
                <div className="h-full flex flex-col items-center justify-center gap-2 text-gray-500">
                    <FileText className="w-10 h-10 opacity-40" />
                    <p className="text-sm">Preview not available for this file type</p>
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
                    >
                        <ExternalLink className="w-4 h-4" />
                        Open file
                    </a>
                </div>
            )}
        </div>
    );
};

const Field = ({ label, children }) => (
    <div>
        <p className="text-[11px] uppercase tracking-wide text-gray-400">{label}</p>
        <p className="text-sm text-gray-900 break-words">{children || '—'}</p>
    </div>
);

const ReviewModal = ({ document: doc, rejectionReasons, mode, onClose, onPositive, onReject, onError }) => {
    const [action, setAction] = useState('');
    const [reasonCode, setReasonCode] = useState('');
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const positiveLabel = mode === 'approve' ? 'Approve' : 'Verify';
    const commentRequired = reasonCode === 'OTHER';
    const canSubmit = action === 'positive'
        ? true
        : action === 'reject' && !!reasonCode && (!commentRequired || comment.trim().length > 0);

    useEffect(() => {
        const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    const handleSubmit = async () => {
        if (!canSubmit || submitting) return;
        setSubmitting(true);
        try {
            if (action === 'positive') {
                await onPositive(doc.id);
            } else {
                await onReject(doc.id, reasonCode, comment.trim());
            }
            onClose();
        } catch (error) {
            console.error('KYC action failed:', error);
            onError(error.message || `Failed to ${action === 'positive' ? positiveLabel.toLowerCase() : 'reject'} the document`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        // The overlay itself must not scroll — otherwise dragging inside the PDF
        // scrolls the whole modal instead of the document.
        <div
            className="fixed inset-0 bg-black/50 z-50 flex justify-center items-center p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-xl shadow-xl w-full max-w-6xl h-[92vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 truncate">
                            {doc.requirementName || 'KYC Document'}
                        </h3>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${STATUS_STYLES[doc.status] || 'bg-gray-100 text-gray-700'}`}>
                            {doc.status}
                        </span>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body: preview left, details + decision right */}
                <div className="flex-1 flex min-h-0">
                    <div className="flex-1 bg-gray-100 border-r border-gray-200 min-w-0 min-h-0">
                        <DocumentPane doc={doc} />
                    </div>

                    <div className="w-[340px] flex-shrink-0 flex flex-col min-h-0">
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Client">{doc.clientName}</Field>
                                <Field label="Company">{doc.companyName}</Field>
                                <Field label="Document No.">{doc.documentNumber}</Field>
                                <Field label="Submitted">{formatDate(doc.createdAt)}</Field>
                                <Field label="Version">v{doc.version || 1}</Field>
                                <Field label="Requirement">{doc.mandatory ? 'Required' : 'Optional'}</Field>
                            </div>
                            {doc.notes && <Field label="Notes">{doc.notes}</Field>}

                            <div className="pt-1">
                                <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1.5">Decision</p>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        onClick={() => setAction('positive')}
                                        className={`px-3 py-2 text-sm font-medium rounded-lg border transition flex items-center justify-center gap-1.5 ${
                                            action === 'positive'
                                                ? 'border-green-600 bg-green-50 text-green-700'
                                                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                                        }`}
                                    >
                                        <CheckCircle className="w-4 h-4" />
                                        {positiveLabel}
                                    </button>
                                    <button
                                        onClick={() => setAction('reject')}
                                        className={`px-3 py-2 text-sm font-medium rounded-lg border transition flex items-center justify-center gap-1.5 ${
                                            action === 'reject'
                                                ? 'border-red-600 bg-red-50 text-red-700'
                                                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                                        }`}
                                    >
                                        <XCircle className="w-4 h-4" />
                                        Reject
                                    </button>
                                </div>
                            </div>

                            {action === 'reject' && (
                                <div className="space-y-2 pt-1">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Reason</label>
                                        <select
                                            value={reasonCode}
                                            onChange={(e) => setReasonCode(e.target.value)}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
                                        >
                                            <option value="">Select a reason...</option>
                                            {rejectionReasons.map((reason) => (
                                                <option key={reason.code} value={reason.code}>{reason.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Comment {commentRequired ? <span className="text-red-500">*</span> : <span className="text-gray-400">(optional)</span>}
                                        </label>
                                        <textarea
                                            value={comment}
                                            onChange={(e) => setComment(e.target.value)}
                                            rows="3"
                                            placeholder="Add context for the client and the audit trail..."
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="border-t border-gray-200 p-3 flex items-center gap-2 flex-shrink-0">
                            <button
                                onClick={handleSubmit}
                                disabled={!action || !canSubmit || submitting}
                                className={`flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 ${
                                    action === 'reject' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                                }`}
                            >
                                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                                {action === 'reject' ? 'Confirm Rejection' : `Confirm ${positiveLabel}`}
                            </button>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KycQueuePage;
